import { useCallback, useContext } from 'react'
import moment from 'moment'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import ExperiencesSearchDateQuickChips, {
  CHIP_ANYTIME,
  type ExperiencesDateQuickChipSelectPayload,
} from 'components/Search/Experiences/ExperiencesSearchDateQuickChips'
import useExperiencesSearchDateDispatch from 'components/Experiences/hooks/useExperiencesSearchDateDispatch'
import useExperiencesSearchFilters from 'components/Experiences/hooks/useExperiencesSearchFilters'
import useTrackExperienceSearchFilter from 'components/Experiences/hooks/useTrackExperienceSearchFilter'
import ExperienceSearchFilterDraftContext from 'contexts/Experience/ExperienceSearchFilterDraftContext'
import { DATE_SEARCH_OPTION_IDS } from 'constants/search'

interface Props {
  showNextBooking?: boolean
  displayType?: 'single-line' | 'multi-line'
}

function ExperienceSearchDateFilter({
  showNextBooking = true,
  displayType = 'multi-line',
}: Props) {
  const globalSearch = useContext(GlobalSearchStateContext)
  const isDraft = !!useContext(ExperienceSearchFilterDraftContext)
  const { selectAnytime, selectDayRange } = useExperiencesSearchDateDispatch()
  const { searchFilters, setSearchFilters } = useExperiencesSearchFilters()
  const trackSearchFilter = useTrackExperienceSearchFilter()
  const checkinDate = isDraft ? (searchFilters.checkIn ? moment(searchFilters.checkIn) : undefined) : globalSearch.checkinDate
  const checkoutDate = isDraft ? (searchFilters.checkOut ? moment(searchFilters.checkOut) : undefined) : globalSearch.checkoutDate
  const dateSearchOptionId = isDraft
    ? (checkinDate ? DATE_SEARCH_OPTION_IDS.SPECIFIC : DATE_SEARCH_OPTION_IDS.ANYTIME)
    : globalSearch.dateSearchOptionId

  const onSelect = useCallback((payload: ExperiencesDateQuickChipSelectPayload) => {
    trackSearchFilter({
      label: 'Date',
      type: 'date',
      value: payload.value,
    })

    if (payload.value === CHIP_ANYTIME || !payload.range) {
      if (!isDraft) selectAnytime()
      setSearchFilters({ ...searchFilters, checkIn: undefined, checkOut: undefined, date: undefined })
      return
    }

    const { start, end } = payload.range
    if (!isDraft) selectDayRange(start, end)
    setSearchFilters({ ...searchFilters, checkIn: start.format(ISO_DATE_FORMAT), checkOut: end.format(ISO_DATE_FORMAT), date: undefined })
  }, [selectAnytime, selectDayRange, searchFilters, setSearchFilters, trackSearchFilter, isDraft])

  return (
    <ExperiencesSearchDateQuickChips
      checkinDate={checkinDate}
      checkoutDate={checkoutDate}
      dateSearchOptionId={dateSearchOptionId}
      onSelect={onSelect}
      displayType={displayType}
      showNextBooking={showNextBooking}
    />
  )
}

export default ExperienceSearchDateFilter
